/**
 * «Мой счёт»: whether the account trades, what stands between it and trading,
 * what the strategy has earned, and how much of that goes back to work.
 *
 * Every state here was decided on the server. The page names it, and offers a
 * control only where pressing it would change something: there is no START
 * button while starting is not allowed, and no pause while nothing runs.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { Panel } from "@/design-system/Panel";
import { StatusBadge, type BadgeTone } from "@/design-system/StatusBadge";
import {
  getBybitConnection,
  pauseClientAccount,
  readClientAccount,
  readStrategyAllocations,
  returnWithheldProfit,
  setClientReinvestment,
} from "@/shared/api";
import type { ClientAccountResponse, ClientEpochResponse } from "@/shared/api";
import { compareDecimal, isZero, money, signedMoney, sumDecimal } from "@/shared/money/money";

import { ACCOUNT_REFRESH_MS } from "@/shared/clients/refresh";

import { StartControl, startStep, tradingControlKey, useTradingControl } from "./StartControl";
import { TimeRecoveryPanel } from "./TimeRecoveryPanel";

type Step = { readonly label: string; readonly state: string; readonly tone: BadgeTone };

const BLOCKER: Readonly<Record<string, string>> = {
  AWAITING_FINAL_ACCEPTANCE_AND_OWNER_START: "ждёт финальной приёмки и вашей команды запуска",
};

/** A blocker the vocabulary does not know is shown as itself, never hidden. */
function blocker(code: string | undefined): string {
  if (code === undefined) return "запуск не разрешён";
  return BLOCKER[code] ?? code;
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
}

function moment(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** The exchange and the strategy, read from their own sources. */
function useReadiness(tenantId: string, accountId: string): { readonly exchange: Step; readonly strategy: Step } {
  const connection = useQuery({
    queryKey: ["bybit-connection", tenantId, accountId],
    retry: false,
    queryFn: async () => {
      const { data } = await getBybitConnection({ credentials: "same-origin", cache: "no-store" });
      if (data === undefined) throw new Error("connection unavailable");
      return data;
    },
  });
  const allocations = useQuery({
    queryKey: ["strategy-allocations", tenantId, accountId],
    retry: false,
    queryFn: async () => {
      const { data } = await readStrategyAllocations();
      if (data === undefined) throw new Error("allocations unavailable");
      return data;
    },
  });

  const c = connection.data;
  const exchange: Step = c === undefined
    ? { label: "Биржа Bybit", state: connection.isError ? "состояние неизвестно" : "проверяю…", tone: "muted" }
    : c.status === "DISCONNECTED"
      ? { label: "Биржа Bybit", state: "не подключена", tone: "muted" }
      : c.execution_state === "PROVEN" || c.status === "LEGACY_CONNECTED"
        ? { label: "Биржа Bybit", state: "подключена и сверена", tone: "ready" }
        : { label: "Биржа Bybit", state: "подключена, идёт сверка", tone: "warning" };

  const a = allocations.data;
  const active = a?.some((item) => item.active_for_new_cycles === true) ?? false;
  const strategy: Step = a === undefined
    ? { label: "Стратегия", state: allocations.isError ? "состояние неизвестно" : "проверяю…", tone: "muted" }
    : active
      ? { label: "Стратегия", state: "назначена и активна", tone: "ready" }
      : a.length > 0
        ? { label: "Стратегия", state: "назначена, не активна", tone: "warning" }
        : { label: "Стратегия", state: "не назначена", tone: "muted" };

  return { exchange, strategy };
}

/** Stopping new cycles; open ones are served to their exit. One definition for
 *  every screen that offers it. */
export function usePauseTrading(tenantId: string, accountId: string, onPaused: () => Promise<void>) {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error, response } = await pauseClientAccount({ body: { command_id: crypto.randomUUID() } });
      if (error !== undefined || response?.ok !== true) throw new Error("pause refused");
    },
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: tradingControlKey(tenantId, accountId) });
      await onPaused();
    },
  });
}

export const clientAccountKey = (tenantId: string, accountId: string) =>
  ["client-account", tenantId, accountId] as const;

/** The account's trading state and result, as the server states them. */
export function useClientAccount(tenantId: string, accountId: string) {
  return useQuery({ queryKey: clientAccountKey(tenantId, accountId), refetchInterval: ACCOUNT_REFRESH_MS, queryFn: async () => {
    const { data, error } = await readClientAccount();
    if (error !== undefined || data === undefined || data.tenant_id !== tenantId || data.account_id !== accountId) {
      throw new Error("account unavailable");
    }
    return data;
  } });
}

function TradingPanel({ data, tenantId, accountId, onPaused }: {
  readonly data: ClientAccountResponse; readonly tenantId: string; readonly accountId: string;
  readonly onPaused: () => Promise<void>;
}): React.JSX.Element {
  const { exchange, strategy } = useReadiness(tenantId, accountId);
  const control = useTradingControl(tenantId, accountId);
  const pause = usePauseTrading(tenantId, accountId, onPaused);
  const launch: Step = control.data === undefined && !control.isError
    ? startStep(undefined, false)
    : control.data === undefined
      ? { label: "Запуск", state: blocker(data.start_blocker), tone: "muted" }
      : startStep(control.data, false);
  const steps = [exchange, strategy, launch];
  const running = data.admissions_enabled;

  return (
    <Panel
      title="Торговля"
      testId="client-account"
      trailing={<StatusBadge tone={running ? "ready" : "muted"}>{running ? "Новые циклы открываются" : "Выключена"}</StatusBadge>}
    >
      <div className="flex flex-col gap-3 text-body-main text-text-secondary">
        {/* Running says so in the badge; only a stopped account needs words. */}
        {!running && (
          <p>Новые циклы не открываются. Подключение биржи и назначение стратегии сами по себе торговлю не запускают.</p>
        )}
        <ol className="divide-y divide-border-subtle" data-testid="trading-steps">
          {steps.map((step, index) => (
            <li key={step.label} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-2 first:pt-0">
              <span className="text-text-primary">{index + 1}. {step.label}</span>
              <StatusBadge tone={step.tone}>{step.state}</StatusBadge>
            </li>
          ))}
        </ol>
        {!running && control.data !== undefined && (
          <StartControl tenantId={tenantId} accountId={accountId} control={control.data} onChanged={onPaused} />
        )}
        {running && data.can_manage && (
          <div className="flex flex-col gap-2">
            <div>
              <Button onClick={() => { pause.mutate(); }} disabled={pause.isPending}>Поставить на паузу</Button>
            </div>
            <p className="text-helper-sm">Новые циклы перестанут открываться; открытые сопровождаются до безопасного выхода.</p>
          </div>
        )}
        {pause.isError && <p role="alert" className="text-status-warning">Пауза не подтверждена. Обновите страницу перед повтором.</p>}
        {data.trading_history.length > 0 && (
          <details className="text-helper-sm">
            <summary className="cursor-pointer select-none">История запусков и пауз</summary>
            <ul className="mt-2 flex flex-col gap-1">
              {data.trading_history.map((item) => (
                <li key={item.command_id}>{moment(item.recorded_at)} · {item.kind === "START" ? "запуск" : item.kind === "PAUSE" ? "пауза" : item.kind}</li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </Panel>
  );
}

function Technical({ epoch }: { readonly epoch: ClientEpochResponse }): React.JSX.Element {
  return (
    <details className="text-helper-sm text-text-muted">
      <summary className="cursor-pointer select-none text-text-secondary">Технические данные</summary>
      <dl className="mt-2 grid gap-1 break-all font-mono text-data-mono">
        <dt>Период</dt><dd>{epoch.epoch_id}</dd>
        <dt>Версия</dt><dd>{epoch.version_id ?? "историческая привязка"}</dd>
        <dt>Назначение</dt><dd>{epoch.allocation_id ?? "не подтверждено"}</dd>
        <dt>Контракт цикла</dt><dd>{epoch.contract_sha256}</dd>
      </dl>
    </details>
  );
}

function ResultPanel({ data }: { readonly data: ClientAccountResponse }): React.JSX.Element {
  const epoch = data.current_epoch;
  return (
    <Panel title="Результат стратегии" testId="strategy-result">
      <div className="flex flex-col gap-3 text-body-main text-text-secondary">
        {epoch === null ? (
          <p>Учёт результата начнётся с первым запуском торговли. Прежняя история сохраняется.</p>
        ) : (
          <>
            <dl className="divide-y divide-border-subtle">
              {[
                ["Учёт с", day(epoch.started_at)],
                ["Результат закрытых циклов, USDT", epoch.strategy_net === null ? "— учёт неполный" : signedMoney(epoch.strategy_net)],
                // One figure when they agree: two rows saying the same number
                // read as two different things (owner, 2026-10-30).
                ...(money(epoch.starting_capital) === money(epoch.starting_equity)
                  ? [["Капитал на старте, USDT", money(epoch.starting_capital)]]
                  : [
                    ["Капитал стратегии на старте, USDT", money(epoch.starting_capital)],
                    ["Средства на счёте на старте, USDT", money(epoch.starting_equity)],
                  ]),
                // Accrued at each close above the high-water mark; shown only
                // where the platform bills the account at all.
                ...(isZero(data.platform_fee ?? "0") ? [] : [["Комиссия платформы начислена, USDT", money(data.platform_fee ?? "0")]]),
              ].map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between gap-3 py-2 first:pt-0">
                  <dt>{label}</dt>
                  <dd className="text-right text-text-primary tabular-nums">{value}</dd>
                </div>
              ))}
            </dl>
            {epoch.incomplete_cycles.length > 0 && (
              <p className="text-status-warning">Циклов с неполным учётом: {epoch.incomplete_cycles.length}. Они не входят в результат, пока учёт не завершён.</p>
            )}
            <p className="text-helper-sm">В результат входят только закрытые и подтверждённые циклы. Пополнения, переводы и сделки вне стратегии не считаются прибылью.</p>
            <Technical epoch={epoch} />
          </>
        )}
        {data.epochs.length > 0 && (
          <details className="text-helper-sm">
            <summary className="cursor-pointer select-none">Прошлые периоды</summary>
            <ul className="mt-2 flex flex-col gap-1">
              {data.epochs.map((item) => (
                <li key={item.epoch_id}>с {day(item.started_at)} · {item.strategy_net === null ? "учёт неполный" : `${signedMoney(item.strategy_net)} USDT`}</li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </Panel>
  );
}

/** The set-aside profit as an account the owner can check against Bybit:
 *  what closes set aside, what withdrawals paid out of it, what is left. */
/**
 * «Добавить к капиталу» (owner, 2026-10-17): profit set aside for withdrawal
 * goes back to work when the owner decides not to take it out. All of what is
 * left, as the server records it; one press is one request.
 */
function ReturnToCapital({ amount, onSaved }: {
  readonly amount: string; readonly onSaved: () => Promise<void>;
}): React.JSX.Element {
  const [asking, setAsking] = useState(false);
  const back = useMutation({
    mutationFn: async () => {
      const { error } = await returnWithheldProfit({ body: { return_id: crypto.randomUUID() } });
      if (error !== undefined) throw new Error("not returned");
    },
    onSuccess: async () => { setAsking(false); await onSaved(); },
  });
  if (!asking) {
    return (
      <Button type="button" variant="secondary" data-testid="withheld-return" onClick={() => { setAsking(true); }}>
        Добавить к капиталу
      </Button>
    );
  }
  return (
    <div className="flex flex-col gap-2" data-testid="withheld-return-confirm">
      <p className="text-text-primary">{money(amount)} USDT вернутся в торговлю и перестанут быть доступны к выводу. Отменить это нельзя.</p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" disabled={back.isPending} onClick={() => { back.mutate(); }}>Добавить</Button>
        <Button type="button" variant="ghost" disabled={back.isPending} onClick={() => { setAsking(false); }}>Отмена</Button>
      </div>
      {back.isError && <p role="alert" className="text-status-warning">Не добавлено. Обновите страницу и попробуйте ещё раз.</p>}
    </div>
  );
}

/** A percentage as a person writes it: no trailing zeros. */
function trimmed(value: string): string {
  return value.includes(".") ? value.replace(/\.?0+$/, "") : value;
}

/** The shares a reader picks from; any other figure is typed. */
const SHARES = ["0", "25", "50", "75", "100"] as const;

/**
 * What is left to withdraw, and where it came from: one figure, with the sum
 * that makes it underneath (owner, 2026-10-17: «непонятно, что вернул, что
 * осталось»). Withdrawals in full one click away.
 */
function WithheldLedger({ policy, canManage, onSaved }: {
  readonly policy: NonNullable<ClientAccountResponse["reinvest_policy"]>;
  readonly canManage: boolean;
  readonly onSaved: () => Promise<void>;
}): React.JSX.Element {
  const left = policy.withheld ?? "0";
  const returned = policy.returned ?? "0";
  const withdrawals = policy.withdrawals ?? [];
  const parts: readonly (readonly [string, string, string])[] = [
    ["отложено", policy.set_aside ?? "0", "withheld-set-aside"],
    ["выведено", policy.withdrawn ?? "0", "withheld-withdrawn"],
    ...(isZero(returned) ? [] : [["возвращено в работу", returned, "withheld-returned"] as const]),
  ];
  return (
    <section className="flex flex-col gap-3 border-t border-border-subtle pt-4" aria-labelledby="withheld-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-testid="reinvest-withheld">
        <h3 id="withheld-heading" className="text-body-main text-text-secondary">К выводу сейчас</h3>
        <p className="font-numeric text-heading-sm tabular-nums text-text-primary">{money(left)} USDT</p>
      </div>
      <p className="font-numeric text-helper-sm tabular-nums text-text-muted" data-testid="withheld-sum">
        {parts.map(([label, amount, testId], index) => (
          <span key={testId} data-testid={testId}>
            {index === 0 ? "" : " − "}
            {label} {money(amount)}
          </span>
        ))}
        <span> = {money(left)}</span>
      </p>
      {canManage && !isZero(left) && <ReturnToCapital amount={left} onSaved={onSaved} />}
      {isZero(left) && (
        <p className="text-helper-sm text-text-muted">Выводить сейчас нечего: вся прибыль работает.</p>
      )}
      <details className="text-helper-sm" data-testid="withheld-withdrawals">
        <summary className="cursor-pointer select-none rounded-row text-text-secondary focus-visible:outline-2 focus-visible:outline-focus-ring">
          Выводы с биржи ({withdrawals.length})
        </summary>
        {withdrawals.length === 0 ? (
          <p className="mt-2 text-text-muted">Выводов с биржи после начала учёта не было.</p>
        ) : (
          <ul className="mt-2 flex flex-col gap-1">
            {withdrawals.map((item) => (
              <li key={`${item.at}-${item.amount}`} className="flex justify-between gap-4">
                <span>{day(item.at)}</span>
                <span className="tabular-nums text-text-primary">{money(item.amount)} USDT</span>
              </li>
            ))}
          </ul>
        )}
      </details>
    </section>
  );
}

function ReinvestPanel({ data, onSaved }: {
  readonly data: ClientAccountResponse; readonly onSaved: () => Promise<void>;
}): React.JSX.Element {
  const current = data.reinvest_policy;
  const [custom, setCustom] = useState("");
  const policy = useMutation({
    mutationFn: async (percent: string) => {
      const { error, response } = await setClientReinvestment({ body: { policy_id: crypto.randomUUID(), percent } });
      if (error !== undefined || response?.ok !== true) throw new Error("policy refused");
    },
    onSuccess: async () => { setCustom(""); await onSaved(); },
  });
  // Exact decimals all the way: a share is compared and subtracted as text.
  const share = current === null ? null : trimmed(current.percent);
  const kept = share === null ? null : trimmed(sumDecimal(["100", `-${share}`]));
  return (
    <Panel title="Прибыль" testId="reinvest">
      <div className="flex flex-col gap-4 text-body-main text-text-secondary">
        <p className="text-text-primary" data-testid="reinvest-split">
          {share === null
            ? "Доля прибыли в работу не задана — вся прибыль остаётся в работе."
            : <>Из каждой прибыли <b className="tabular-nums">{share} %</b> идёт в работу, <b className="tabular-nums">{kept} %</b> откладывается к выводу.</>}
        </p>
        {data.can_manage ? (
          <form
            className="flex flex-col gap-2"
            aria-label="Доля прибыли в работу"
            onSubmit={(event) => { event.preventDefault(); if (custom !== "" && !policy.isPending) policy.mutate(custom); }}
          >
            <div role="group" aria-label="Готовые доли прибыли в работу" className="flex w-fit flex-wrap gap-0.5 rounded-row bg-background-base p-0.5">
              {SHARES.map((value) => {
                const active = share !== null && compareDecimal(value, share) === 0;
                return (
                  <Button
                    key={value}
                    type="button"
                    variant="ghost"
                    selected={active}
                    aria-pressed={active}
                    disabled={policy.isPending}
                    onClick={() => { if (!active) policy.mutate(value); }}
                    // The share in force carries the accent: a selected
                    // ghost's tint alone did not read on the dark tray.
                    className={`min-w-14 tabular-nums ${active ? "text-action-primary ring-1 ring-action-primary" : ""}`}
                  >
                    {value} %
                  </Button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                aria-label="Доля прибыли в работу, %"
                placeholder="Своя доля, %"
                className="min-h-9 w-36 rounded-row border border-border-strong bg-surface-default px-2 text-text-primary transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-focus-ring"
                type="number" min="0" max="100" step="any"
                value={custom}
                onChange={(event) => { setCustom(event.target.value); }}
              />
              {custom !== "" && <Button type="submit" disabled={policy.isPending}>Сохранить</Button>}
            </div>
          </form>
        ) : (
          <p className="text-helper-sm text-text-muted" data-testid="reinvest-owner-only">Долю меняет владелец счёта под своим логином.</p>
        )}
        {policy.isError && <p role="alert" className="text-status-warning">Не сохранено. Обновите страницу и попробуйте ещё раз.</p>}
        <p className="text-helper-sm text-text-muted">
          Новая доля действует для сделок, закрытых после сохранения{current === null ? "" : `; текущая — с ${day(current.effective_at)}`}.
        </p>
        {current !== null && <WithheldLedger policy={current} canManage={data.can_manage} onSaved={onSaved} />}
      </div>
    </Panel>
  );
}

/**
 * What the client decides about its own profit: how much goes back into
 * trading, and whether free capital buys into stuck cycles first. The same two
 * panels the owner's «Мой счёт» carries, for the client's «Настройки» — the
 * cabinet once left them out and a client had no way to set either
 * (owner, 2026-10-16).
 */
export function ClientProfitSettings({ tenantId, accountId }: {
  readonly tenantId: string; readonly accountId: string;
}): React.JSX.Element {
  const queries = useQueryClient();
  const account = useClientAccount(tenantId, accountId);
  const refresh = async (): Promise<void> => {
    await queries.invalidateQueries({ queryKey: clientAccountKey(tenantId, accountId) });
  };
  const data = account.data;
  if (data === undefined || account.isError) {
    return (
      <Panel title="Реинвестирование прибыли" testId="reinvest">
        <p role="status">Данные счёта {account.isError ? "недоступны" : "загружаются"}.</p>
      </Panel>
    );
  }
  return (
    <>
      <ReinvestPanel data={data} onSaved={refresh} />
      <TimeRecoveryPanel data={data} onSaved={refresh} />
    </>
  );
}

function useAccountView(tenantId: string, accountId: string) {
  const queries = useQueryClient();
  const account = useClientAccount(tenantId, accountId);
  const refresh = async (): Promise<void> => {
    await queries.invalidateQueries({ queryKey: clientAccountKey(tenantId, accountId) });
  };
  return { account, refresh };
}

function Unavailable({ title, failed }: { readonly title: string; readonly failed: boolean }): React.JSX.Element {
  return <Panel title={title}><p role="status">Данные счёта {failed ? "недоступны" : "загружаются"}.</p></Panel>;
}

/** Whether the account trades, and what it has earned doing it. */
export function AccountTrading({ tenantId, accountId }: {
  readonly tenantId: string; readonly accountId: string;
}): React.JSX.Element {
  const { account, refresh } = useAccountView(tenantId, accountId);
  const data = account.data;
  if (data === undefined || account.isError) return <Unavailable title="Торговля" failed={account.isError} />;
  return (
    <>
      <TradingPanel data={data} tenantId={tenantId} accountId={accountId} onPaused={refresh} />
      <ResultPanel data={data} />
    </>
  );
}

/** What happens to the account's money: its profit share, recovery buys. */
export function AccountMoney({ tenantId, accountId }: {
  readonly tenantId: string; readonly accountId: string;
}): React.JSX.Element {
  const { account, refresh } = useAccountView(tenantId, accountId);
  const data = account.data;
  if (data === undefined || account.isError) return <Unavailable title="Прибыль" failed={account.isError} />;
  return (
    <>
      <ReinvestPanel data={data} onSaved={refresh} />
      <TimeRecoveryPanel data={data} onSaved={refresh} />
    </>
  );
}
